import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Building2, Globe, MapPin, Users, Phone, Mail, 
  Plus, Pencil, Trash2, Calendar, TrendingUp, FileText,
  DollarSign, Target, Award
} from 'lucide-react';
import { toast } from 'sonner';
import ContactsTab from '../components/client-detail/ContactsTab';
import InteractionsTab from '../components/client-detail/InteractionsTab';
import ProjectsTab from '../components/client-detail/ProjectsTab';
import OffresTab from '../components/client-detail/OffresTab';
import ClientInfoModal from '../components/client-detail/ClientInfoModal';

export default function ClientDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadClientData();
  }, [id]);

  const loadClientData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${id}/details`);
      setClientData(response.data);
    } catch (error) {
      console.error('Error loading client:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${id}`);
      toast.success('Client supprimé avec succès');
      navigate('/clients');
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatutBadgeColor = (statut) => {
    const colors = {
      'Prospect': 'bg-yellow-100 text-yellow-700',
      'Actif': 'bg-green-100 text-green-700',
      'Inactif': 'bg-gray-100 text-gray-700',
      'Partenaire stratégique': 'bg-blue-100 text-blue-700'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="clients">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!clientData) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="clients">
        <div className="text-center py-12">
          <p className="text-gray-600">Client non trouvé</p>
          <Button onClick={() => navigate('/clients')} className="mt-4">
            Retour à la liste
          </Button>
        </div>
      </Layout>
    );
  }

  const { client, contacts, interactions, projets, offres } = clientData;

  return (
    <Layout user={user} onLogout={onLogout} currentPage="clients">
      <div className="space-y-6">
        {/* Header avec actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/clients')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{client.entreprise}</h1>
              <p className="text-gray-600">{client.secteur}</p>
            </div>
            <Badge className={getStatutBadgeColor(client.statut)}>
              {client.statut}
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEditModal(true)}
              className="gap-2"
            >
              <Pencil className="h-4 w-4" />
              Modifier
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteClient}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Contrats actifs</p>
                  <p className="text-2xl font-bold">{client.nb_contrats_actifs || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">CA Total</p>
                  <p className="text-2xl font-bold">{(client.ca_total_genere || 0).toLocaleString()} GNF</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Contacts</p>
                  <p className="text-2xl font-bold">{contacts?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Dernière interaction</p>
                  <p className="text-sm font-semibold">
                    {client.derniere_interaction 
                      ? new Date(client.derniere_interaction).toLocaleDateString()
                      : 'Aucune'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Informations générales */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Informations Générales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-600">Localisation</p>
                  <p className="font-medium">{client.ville}, {client.pays || 'Guinée'}</p>
                  <p className="text-sm text-gray-500">{client.adresse}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-600">Téléphone</p>
                  <p className="font-medium">{client.telephone}</p>
                </div>
              </div>

              {client.site_web && (
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Site web</p>
                    <a 
                      href={client.site_web} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {client.site_web}
                    </a>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-600">Taille entreprise</p>
                  <p className="font-medium">{client.taille_entreprise || 'PME'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm text-gray-600">Commercial responsable</p>
                  <p className="font-medium">{client.commercial}</p>
                </div>
              </div>

              {client.taux_satisfaction && (
                <div className="flex items-start gap-3">
                  <Award className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Taux de satisfaction</p>
                    <p className="font-medium">{client.taux_satisfaction}%</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Onglets */}
        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="contacts">
              <Users className="h-4 w-4 mr-2" />
              Contacts Clés ({contacts?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="interactions">
              <Calendar className="h-4 w-4 mr-2" />
              Historique ({interactions?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="projets">
              <Target className="h-4 w-4 mr-2" />
              Projets ({projets?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="offres">
              <FileText className="h-4 w-4 mr-2" />
              Offres ({offres?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contacts">
            <ContactsTab 
              clientId={id} 
              contacts={contacts} 
              onUpdate={loadClientData}
            />
          </TabsContent>

          <TabsContent value="interactions">
            <InteractionsTab 
              clientId={id} 
              interactions={interactions} 
              contacts={contacts}
              onUpdate={loadClientData}
            />
          </TabsContent>

          <TabsContent value="projets">
            <ProjectsTab 
              clientId={id} 
              projets={projets} 
              onUpdate={loadClientData}
            />
          </TabsContent>

          <TabsContent value="offres">
            <OffresTab 
              clientId={id} 
              offres={offres} 
              projets={projets}
              client={client}
              onUpdate={loadClientData}
            />
          </TabsContent>
        </Tabs>

        {/* Modal d'édition des infos client */}
        {showEditModal && (
          <ClientInfoModal
            client={client}
            onClose={() => setShowEditModal(false)}
            onSuccess={() => {
              loadClientData();
              setShowEditModal(false);
            }}
          />
        )}
      </div>
    </Layout>
  );
}
